import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack,
  TextField, Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { ConfirmationDialog, useOptionalPermissionCenter } from '@1823-partners/core';

import {
  deleteRemittanceFile, fetchRemittanceFiles, uploadRemittanceFile,
} from '../../api/remittance';
import RemittanceFilesGrid from './RemittanceFilesGrid';

/** Granted by the recon_remittance_runner role; see pam/setup/permissions/bootstrap_seed.py. */
const INGEST = 'recon.remittance:ingest';

// Matches the engine's ACCEPTED_SUFFIXES. The toolbar's own file dialog filters on its default
// '.csv,.xlsx,.xls'; this is the second check, for a file that arrives by drop or a renamed one.
const ACCEPTED_SUFFIXES = ['.csv', '.xls', '.xlsx'];

// remittance/ingest.py MAX_CONTENT_BYTES. A courtesy check so an oversized workbook fails here
// instead of after the upload; the server stays the authority and answers 413 either way.
const MAX_CONTENT_BYTES = 8 * 1024 * 1024;

// How many months to offer. Long enough to re-file a stale month, short enough to stay a menu.
const MONTH_CHOICES = 18;

/**
 * Every month somebody might upload to, newest first.
 *
 * Generated rather than read from the server, because /remittance-periods answers with the months
 * that ALREADY HAVE FILES -- which is every month except the one being uploaded to. Periods with
 * files are still unioned in so an older month stays reachable once it drops out of the window.
 */
export const monthOptions = (periodsWithFiles = []) => {
  const now = dayjs();
  const recent = Array.from({ length: MONTH_CHOICES }, (_, index) =>
    now.subtract(index, 'month').format('YYYY-MM'));
  return Array.from(new Set([...recent, ...periodsWithFiles])).sort().reverse();
};

/**
 * The dedupe identity of an uploaded file.
 *
 * The server keys a file version on `source_key(file_name)::sha256(source_id)`, so this value
 * decides whether a second upload REPLACES the first or joins it. Derived from the file name so a
 * corrected workbook uploaded under the same name supersedes the version it corrects; a
 * per-upload value would leave both active and count the month's cash twice.
 *
 * The extension is stripped for the same reason `layout.source_key` strips it: the same report
 * arriving once as .xlsx and later as .csv is one logical file, not two.
 *
 * This does NOT collide with the intake agent's key for the same attachment -- the agent's
 * source_id is the mail attachment's id, not a file name. Identical content is still caught, by
 * the server's cross-key duplicate check, which refuses it as `duplicate_content`.
 */
export const sourceIdFor = (fileName) => {
  const name = String(fileName || '').trim();
  const lowered = name.toLowerCase();
  const suffix = ACCEPTED_SUFFIXES.find((candidate) => lowered.endsWith(candidate));
  const stem = suffix ? name.slice(0, -suffix.length) : name;
  return `upload:${stem.split(/\s+/).join(' ').toLowerCase()}`;
};

/** Why this file cannot be sent, or null. */
const rejection = (file) => {
  const lowered = String(file.name || '').toLowerCase();
  if (!ACCEPTED_SUFFIXES.some((suffix) => lowered.endsWith(suffix))) {
    return `the engine reads ${ACCEPTED_SUFFIXES.join(', ')}, not this file type`;
  }
  if (!file.size) return 'the file is empty';
  if (file.size > MAX_CONTENT_BYTES) {
    return `it is ${file.size.toLocaleString()} bytes; the limit is ${MAX_CONTENT_BYTES.toLocaleString()}`;
  }
  return null;
};

/**
 * One upload's outcome, as the reviewer needs to read it.
 *
 * A 202 does not mean the file was stored, and being stored does not mean it can be read. Those
 * are three different outcomes and they are graded differently: an unreadable file is stored, is
 * reported as a success by the transport, and contributes no cash whatsoever to a run.
 */
const describeResult = (fileName, body) => {
  const stored = !!body?.stored;
  const unreadable = !!body?.parse_error || body?.stream === 'unrecognized';
  const superseded = body?.superseded_source_key
    ? ` Retired ${body.superseded_source_key}.`
    : '';

  if (stored && unreadable) {
    return {
      fileName,
      severity: 'warning',
      text: `Stored, but the parser could not read it${body.parse_error ? `: ${body.parse_error}` : ''}.`
        + ' It will contribute nothing to a run until it is replaced.',
    };
  }
  if (stored) {
    return {
      fileName,
      severity: 'success',
      text: `Stored as ${body.stream} — ${(body.rows_parsed ?? 0).toLocaleString()} row(s).${superseded}`,
    };
  }
  if (body?.reason === 'unchanged') {
    return {
      fileName,
      severity: 'info',
      text: `Already stored, unchanged — nothing was written.${superseded}`,
    };
  }
  if (body?.reason === 'duplicate_content') {
    return {
      fileName,
      severity: 'info',
      text: `The same data is already stored as ${body.duplicate_of} — nothing was written.`
        + `${body.retired_stale_version ? ' A stale version of this file was retired.' : ''}${superseded}`,
    };
  }
  return {
    fileName,
    severity: 'info',
    text: `Not stored${body?.reason ? `: ${body.reason}` : ''}.`,
  };
};

/** The server's message for a refused upload, preferring its own words over axios's. */
const describeFailure = (fileName, err) => {
  const body = err?.response?.data;
  if (err?.response?.status === 413) {
    const limit = body?.limit;
    return {
      fileName,
      severity: 'error',
      text: 'Too large for one request'
        + `${limit ? ` — the limit is ${Number(limit).toLocaleString()} bytes` : ''}.`,
    };
  }
  return {
    fileName,
    severity: 'error',
    text: body?.message || err?.message || 'The upload failed.',
  };
};

/**
 * Put a month's servicer files in, and see what the parser made of them.
 *
 * Uploading does NOT start a run: the engine reads these files when a run is started, and keeping
 * the two apart is deliberate -- a month is usually incomplete for several days, and a run over a
 * half-arrived month produces differences that are an artefact of the upload order.
 */
const RemittanceUploadPanel = ({ periods = [], onStored, height = 240 }) => {
  // Optional, not usePermissionCenter: this panel renders without a provider in several tests, and
  // the throwing hook would turn a missing provider into a crash rather than a lost hint.
  const permissionCenter = useOptionalPermissionCenter();
  const canIngest =
    !permissionCenter || permissionCenter.loading || permissionCenter.error
      ? true
      : permissionCenter.hasPermission(INGEST);

  const months = useMemo(() => monthOptions(periods), [periods]);
  // Last month, not this one: a month's remittances arrive after it closes, so the month being
  // filed is almost never the current one.
  const [month, setMonth] = useState(() => dayjs().subtract(1, 'month').format('YYYY-MM'));
  const [fileData, setFileData] = useState({ period: null, rows: [] });
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [results, setResults] = useState([]);
  const [filesExpanded, setFilesExpanded] = useState(false);
  // Results are prepended, so anything derived from position renumbers the whole list on every
  // upload. This only ever counts up.
  const resultId = useRef(0);
  const loadFilesRequest = useRef(0);

  // Never render rows fetched for another month, including while this month's request is pending.
  const files = fileData.period === month ? fileData.rows : [];

  const loadFiles = useCallback(async (target) => {
    if (!target) return;
    const requestId = ++loadFilesRequest.current;
    setLoadingFiles(true);
    setFilesError(null);
    try {
      const response = await fetchRemittanceFiles(target);
      if (requestId !== loadFilesRequest.current) return;
      setFileData({
        period: target,
        rows: Array.isArray(response.data) ? response.data : [],
      });
    } catch (err) {
      if (requestId !== loadFilesRequest.current) return;
      setFileData({ period: target, rows: [] });
      setFilesError(err?.response?.data?.message || err?.message || 'Could not list the files.');
    } finally {
      if (requestId === loadFilesRequest.current) setLoadingFiles(false);
    }
  }, []);

  useEffect(() => {
    loadFiles(month);
    // The selection belongs to the month it was made in. Carrying it across would aim the delete
    // button at a key this month does not have, which the server answers as `not_active`.
    setSelected(null);
    return () => {
      // Invalidate a request for the previous month, or any request still pending on unmount.
      loadFilesRequest.current += 1;
    };
  }, [month, loadFiles]);

  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    // Redundant with showUpload, deliberately: the permission center resolves after first paint,
    // so a click that lands in that window would otherwise reach the server ungated.
    if (!canIngest) {
      setResults((prior) => [
        { id: ++resultId.current, fileName: file.name, severity: 'error', text: `You do not have ${INGEST}.` },
        ...prior,
      ]);
      return;
    }
    const refused = rejection(file);
    if (refused) {
      setResults((prior) => [
        { id: ++resultId.current, fileName: file.name, severity: 'error', text: `Not sent — ${refused}.` },
        ...prior,
      ]);
      return;
    }

    setUploading(file.name);
    try {
      const response = await uploadRemittanceFile(month, file, sourceIdFor(file.name));
      setResults((prior) => [
        { id: ++resultId.current, ...describeResult(file.name, response.data) },
        ...prior,
      ]);
      await loadFiles(month);
      // Only a stored file makes a month runnable, but the parent refreshes on any accepted upload:
      // a duplicate under a different key still means this month has files, and the period list is
      // cheap to re-read compared with a month that silently refuses to appear.
      onStored?.(month);
    } catch (err) {
      setResults((prior) => [
        { id: ++resultId.current, ...describeFailure(file.name, err) },
        ...prior,
      ]);
    } finally {
      setUploading(null);
    }
  }, [canIngest, month, loadFiles, onStored]);

  const closeDelete = useCallback(() => {
    setDeleteOpen(false);
    setDeleteReason('');
  }, []);

  const handleDelete = useCallback(async () => {
    if (!selected) return;
    const { SOURCE_KEY: sourceKey, FILE_NAME: fileName } = selected;
    setDeleting(true);
    try {
      await deleteRemittanceFile(month, sourceKey, deleteReason.trim());
      setResults((prior) => [
        {
          id: ++resultId.current,
          fileName,
          severity: 'info',
          text: `Deleted from ${month}. Runs of this month will no longer read it;`
            + ' runs already recorded are unchanged.',
        },
        ...prior,
      ]);
      setSelected(null);
      closeDelete();
      await loadFiles(month);
      // Deleting the LAST file in a month makes it unrunnable, so the run selector has to be told
      // for the same reason an upload tells it.
      onStored?.(month);
    } catch (err) {
      const body = err?.response?.data;
      setResults((prior) => [
        {
          id: ++resultId.current,
          fileName,
          severity: 'error',
          text: body?.message || err?.message || 'The delete failed.',
        },
        ...prior,
      ]);
      closeDelete();
    } finally {
      setDeleting(false);
    }
  }, [selected, month, deleteReason, closeDelete, loadFiles, onStored]);

  const filesSummary = loadingFiles
    ? `Loading ${month}…`
    : filesError
      ? `${month} · File list unavailable`
      : `${files.length} file${files.length === 1 ? '' : 's'} for ${month}`;

  return (
    <Box sx={{ display: 'contents' }}>
      <Button
        variant="outlined"
        color="inherit"
        fullWidth
        onClick={() => setFilesExpanded((expanded) => !expanded)}
        aria-expanded={filesExpanded}
        aria-controls="remittance-servicer-files"
        endIcon={filesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        data-testid="remittance-files-toggle"
        sx={{
          gridArea: 'filesToggle',
          minWidth: 0,
          justifyContent: 'space-between',
          textTransform: 'none',
          px: 1.5,
          py: 0.75,
        }}
      >
        <Box component="span" sx={{ textAlign: 'left' }}>
          <Typography component="span" variant="subtitle2" fontWeight={700}>
            Servicer files
          </Typography>
          <Typography
            component="span"
            variant="caption"
            color={filesError ? 'warning.main' : 'text.secondary'}
            data-testid="remittance-files-summary"
            sx={{ ml: 1 }}
          >
            {filesSummary}
          </Typography>
        </Box>
      </Button>

      {filesExpanded && (
        <Box
          id="remittance-servicer-files"
          data-testid="remittance-files-content"
          sx={{
            gridArea: 'filesContent',
            minWidth: 0,
            mt: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="remittance-upload-month-label">Upload to month</InputLabel>
              <Select
                labelId="remittance-upload-month-label"
                label="Upload to month"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
                disabled={!!uploading}
                inputProps={{ 'data-testid': 'remittance-upload-month-select' }}
              >
                {months.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {uploading && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Uploading {uploading}…
                </Typography>
              </Stack>
            )}
            {selected && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="remittance-selected-file"
              >
                Selected: {selected.FILE_NAME}
              </Typography>
            )}
            {!canIngest && (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="remittance-ingest-denied"
              >
                You do not have permission to upload remittance files — request the
                recon_remittance_runner role from the permissions menu.
              </Typography>
            )}
          </Stack>

          {filesError && (
            <Alert severity="warning">
              {filesError} A run still reads whatever is stored; this list is what cannot be shown.
            </Alert>
          )}

          {results.map((result) => (
            <Alert key={result.id} severity={result.severity}>
              <strong>{result.fileName}</strong> — {result.text}
            </Alert>
          ))}

          <RemittanceFilesGrid
            files={files}
            // Also while uploading: GridToolbar disables its upload button on isLoading, which is
            // the house way to stop a second file being posted before the first has landed. The
            // list is genuinely stale in that window too -- it is re-read when the upload returns.
            isLoading={loadingFiles || !!uploading}
            canIngest={canIngest}
            onUpload={handleUpload}
            selectedSourceKey={selected?.SOURCE_KEY || null}
            onSelectFile={setSelected}
            onDeleteFile={() => setDeleteOpen(true)}
            height={height}
          />
        </Box>
      )}

      <ConfirmationDialog
        open={deleteOpen}
        onClose={closeDelete}
        onConfirm={handleDelete}
        title="Delete servicer file"
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        confirmColor="error"
        message={(
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{selected?.FILE_NAME}</strong> will stop being read by runs of {month}.
              Reconciliations already recorded are unchanged, and the file stays in the audit
              trail — nothing is destroyed.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              label="Reason (optional)"
              placeholder="e.g. May remittance filed under August by mistake"
              value={deleteReason}
              onChange={(event) => setDeleteReason(event.target.value)}
              variant="outlined"
            />
          </Box>
        )}
      />
    </Box>
  );
};

export default RemittanceUploadPanel;
